import { CityCfg, FeatureCfg, OverrideCfg, PageMeta } from './seo-config.types';
import {
  AVERAGE_TRAVEL_TIME_TO_WORK,
  CRIME_RATE,
  CUMULATIVE_TRAFFIC_INTENSITY,
  GENDER_PAY_GAP,
  HEART_DISEASES_PREVALENCE,
  HOME_OWNERSHIP_RATIO,
  LIFE_EXPECTANCY,
  MEDIAN_AGE,
  MEDIAN_INCOME,
  OBESITY_RATE,
  PEOPLE_PER_MI2,
  POPULATION,
  POPULATION_GROWTH_TREND,
  SMOKING_RATE,
  WISE_INDEX_BY_COUNTRY,
} from '../shared/types/feature-data-type';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  STATE_LEVEL_LAYER,
} from '../map/mapbox/services/layer-store.service';

//TODO run export:seo script on each update here to generate static json,
// which can be used for sitemaps generation

export const FEATURES: Record<string, FeatureCfg> = {
  'crime-rate': {
    path: 'crime-rate',
    cityLabel: 'crime rate map',
    cityBlurb: 'Compare violent, property and community crime by area.',
    featureConst: CRIME_RATE,
    name: 'Crime rate map USA - by county and neighborhood',
    description:
      'Explore the US crime rate map by county and 0.5-mile neighborhood. See total, violent, property, community and business crime per 100,000 residents.',
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  population: {
    path: 'population',
    cityLabel: 'population map',
    cityBlurb: 'See where residents cluster and how density shifts.',
    featureConst: POPULATION,
    name: 'Population map USA - by state, county and neighborhood',
    description:
      'Explore the US population map from state and county down to 0.5-mile neighborhoods. Compare population with income, age and housing data.',
    keywords: [
      'population map',
      'us population map',
      'population map usa',
      'population by county map',
      'population map by neighborhood',
    ],
    level: COUNTY_LEVEL_LAYER,
  },

  'gender-pay-gap': {
    path: 'gender-pay-gap',
    cityLabel: 'gender pay gap map',
    cityBlurb: 'See where men and women earn most and least alike.',
    featureConst: GENDER_PAY_GAP,
    name: 'Gender pay gap map US - by state and neighborhood',
    description:
      'Explore the gender pay gap map of the US. See the percentage difference between median full-time earnings of men and women, down to 0.5-mile areas.',
    keywords: [
      'gender pay gap map',
      'gender pay gap by state',
      'gender wage gap map',
      'pay gap by county map',
    ],
    level: COUNTY_LEVEL_LAYER,
  },

  'life-expectancy': {
    path: 'life-expectancy',
    cityLabel: 'life expectancy map',
    cityBlurb: 'Compare it against local income, health and lifestyle.',
    featureConst: LIFE_EXPECTANCY,
    name: 'Life expectancy map US - by county and neighborhood',
    description:
      'Explore the US life expectancy map by county and 0.5-mile neighborhood. Compare how long people live against local income, health and lifestyle data.',
    keywords: [
      'life expectancy map',
      'us life expectancy map',
      'life expectancy by county map',
      'life expectancy by state map',
    ],
    level: COUNTY_LEVEL_LAYER,
  },

  'commute-time': {
    path: 'commute-time',
    cityLabel: 'commute time map',
    cityBlurb: 'See travel time to work before you move or sign a lease.',
    featureConst: AVERAGE_TRAVEL_TIME_TO_WORK,
    name: 'Commute time map US - average travel time to work',
    description:
      'Explore the US commute time map. See average travel time to work by state, county and 0.5-mile neighborhood, next to income and housing data.',
    keywords: [
      'commute map',
      'commute time map',
      'commute time map usa',
      'commuting map',
      'commuter map',
      'average commute time',
      'travel time to work map',
      'commute times by city',
      'commute times by state',
      'commute times by neighborhood',
    ],
    level: COUNTY_LEVEL_LAYER,
  },
  'crime-map': {
    path: 'crime-map',
    singlePage: true,
    featureConst: CRIME_RATE,
    name: 'Crime map USA | Map AI',
    description:
      'Get clear U.S. crime rate map by category and county. Total crime, community, business, violent, property.',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'criminal-map-usa': {
    path: 'criminal-map-usa',
    singlePage: true,
    featureConst: CRIME_RATE,
    name: 'Map AI: Criminal map USA',
    description:
      'Get clear U.S. crime rate map by category and county. Total crime, community, business, violent, property.',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'crime-mapping-usa': {
    path: 'crime-mapping-usa',
    singlePage: true,
    canonicalPath: 'crime-map',
    featureConst: CRIME_RATE,
    name: 'Crime map USA | Map AI',
    description:
      'Explore crime data and statistics with an interactive map: community crime rate, business crime rate,  violent crime rate, property crime rate. ',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'median-income-map': {
    path: 'median-income-map',
    cityLabel: 'median income map',
    cityBlurb: 'Compare spending power block by block.',
    featureConst: MEDIAN_INCOME,
    name: 'Map of median household income - by neighborhood',
    description:
      'Explore the US median household income map, from census tracts to 0.5-mile areas. Includes demographics, housing and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/5+median+income+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/5+median+income+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/5+median+income+map.jpg',
    },
  },

  'median-household-income-neighborhood': {
    path: 'median-household-income-neighborhood',
    cityLabel: 'median household income map by neighborhood',
    cityBlurb: 'Compare household income between neighborhoods.',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income map by neighborhood',
    description:
      'Explore the US median household income map, from census tracts to 0.5-mile areas. Includes demographics, housing and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'median-household-income-by-census-tract': {
    path: 'median-household-income-by-census-tract',
    cityLabel: 'median household income map by census tract',
    cityBlurb: 'Compare household income tract by tract.',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income by census tract map',
    description:
      'Explore the US median household income map, from census tracts to 0.5-mile areas. Includes demographics, housing and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'education-rankings-by-state': {
    path: 'education-rankings-by-state',
    singlePage: true,
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Education rankings by state - US map & neighborhood data',
    description:
      'Explore the education rankings map by state and down to neighborhood. See high school rates, public school quality and education completion levels.',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/8+states+ranked+by+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/8+states+ranked+by+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/8+states+ranked+by+education.jpg',
    },
  },

  'education-rankings-by-state-map-ai': {
    path: 'education-rankings-by-state-map-ai',
    singlePage: true,
    canonicalPath: 'education-rankings-by-state',
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Rankings for education by state map | Map AI',
    description:
      'Explore the education rankings map by state and down to neighborhood. See high school rates, public school quality and education completion levels.',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/8+states+ranked+by+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/8+states+ranked+by+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/8+states+ranked+by+education.jpg',
    },
  },

  'median-income-by-state': {
    path: 'median-income-by-state',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'Median income by state map - down to 0.5 mi neighborhood',
    description:
      '2024 median income by state on an interactive map, with yearly trends and local data down to 0.5-mi areas across the US.',
    keywords: [
      'median income by state',
      'median average income by state',
      'median household income by state',
      'median family income by state',
      'states by median income',
      'state by state median income',
      'united states median income by year',
      'family median income by state',
      'highest median household income by state',
      'household median income by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/10+Median+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/10+Median+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/10+Median+income+by+state.jpg',
    },
  },

  'homeownership-rate-usa': {
    path: 'homeownership-rate-usa',
    singlePage: true,
    featureConst: HOME_OWNERSHIP_RATIO,
    name: 'Homeownership rate map USA, 2024 | Map AI',
    description:
      'Explore homeownership rates in the USA with our interactive map, down to 0.5-mile areas. Access data on demographics, income, safety and more.',
    keywords: ['rate of homeownership in US', 'us rates of homeownership'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/11+us+rates+of+homeownership.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/11+us+rates+of+homeownership.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/11+us+rates+of+homeownership.jpg',
    },
  },

  'median-age-by-state': {
    path: 'median-age-by-state',
    singlePage: true,
    featureConst: MEDIAN_AGE,
    name: 'Median age by state map | Map AI',
    description:
      'Explore the median age by state map, down to 0.5 mi. See census-based age, median income and real estate insights, interactive and easy to compare.',
    keywords: [
      'median age by state',
      'median age for usa every state',
      'median age by state 2024',
      'median age by state census',
      'median age by us state',
      'median income by age and state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/12+median+age+for+usa+every+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/12+median+age+for+usa+every+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/12+median+age+for+usa+every+state.jpg',
    },
  },

  'smoking-rates-by-city': {
    path: 'smoking-rates-by-city',
    singlePage: true,
    featureConst: SMOKING_RATE,
    name: 'Smoking rates by city map US | Map AI',
    description:
      'Explore smoking rates by city on an interactive map, plus health, income and demographic data down to 0.5-mile areas.',
    keywords: ['smoking rates by city', 'smoking rates map'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/13+boston+smoking+rates.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/13+boston+smoking+rates.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/13+boston+smoking+rates.jpg',
    },
  },

  'population-density-by-state': {
    path: 'population-density-by-state',
    singlePage: true,
    featureConst: PEOPLE_PER_MI2,
    name: 'Population density map by state - down to 0.5 mi neighborhood',
    description:
      'Explore our interactive population density map by state, zooming down to the 0.5-mile neighborhood level.',
    keywords: [
      'population density map by state',
      'states by population density',
      'population density by state',
      'us states by population density',
      'population density by us state',
      'u.s. states by population density',
      'united states population density by state',
      'us population density by state',
      'states ranked by population density',
      'us states ranked by population density',
      'lowest population density by state',
      'list of us states by population density',
      'us state by population density',
      'largest states by population density',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/14+population+density+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/14+population+density+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/14+population+density+by+state.jpg',
    },
  },

  'states-by-population-density': {
    path: 'states-by-population-density',
    singlePage: true,
    canonicalPath: 'population-density-by-state',
    featureConst: PEOPLE_PER_MI2,
    name: 'States by population density map | Map AI',
    description:
      'Explore our interactive population density map by state, zooming down to the 0.5-mile neighborhood level.',
    keywords: [
      'population density map by state',
      'states by population density',
      'population density by state',
      'us states by population density',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/14+population+density+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/14+population+density+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/14+population+density+by+state.jpg',
    },
  },

  'us-states-by-median-income': {
    path: 'us-states-by-median-income',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'US states by median income map | Map AI',
    description:
      'US states by median income on an interactive map, down to 0.5-mile areas. Explore income growth, gender gaps and distribution.',
    keywords: [
      'us states by median income',
      'u.s. states by median income',
      'lowest median income by state',
      'median income by state',
      'median average income by state',
      'median household income by state',
      'median family income by state',
      'states by median income',
      'state by state median income',
      'united states median income by year',
      'family median income by state',
      'highest median household income by state',
      'household median income by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/16+median+average+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/16+median+average+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/16+median+average+income+by+state.jpg',
    },
  },

  'median-household-income-for-each-state': {
    path: 'median-household-income-for-each-state',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'Median income by state ranking map | Map AI',
    description:
      'Median income by state ranking on a US map, as of 2024: #1 Maryland, #2 Massachusetts, #3 New Jersey.',
    keywords: [
      'median income by state',
      'median household income by state',
      'states by median income',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/16+median+average+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/16+median+average+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/16+median+average+income+by+state.jpg',
    },
  },

  'median-income-by-year': {
    path: 'median-income-by-year',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'Median income by year - US map by area',
    description:
      'Track median income by year across the US with Map AI. Explore trends over time on interactive maps, by state, neighborhood and down to 0.5-mile areas.',
    keywords: ['real median income by year data', 'median income by year'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/18+real+median+income+by+year+data.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/18+real+median+income+by+year+data.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/18+real+median+income+by+year+data.jpg',
    },
  },

  'neighborhood-crime-report': {
    path: 'neighborhood-crime-report',
    cityLabel: 'neighborhood crime map',
    cityBlurb: 'Check an address before you move or invest.',
    featureConst: CRIME_RATE,
    name: 'Neighborhood crime report & crime map | Map AI',
    description:
      'Get a free neighborhood crime report and crime map, plus local data on demographics, income and real estate. Know more before you move or invest.',
    keywords: ['neighborhood crime report', 'neighborhood crime map'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/19+Neighborhood+crime+report.jpg',
      classic1200x900:
        'hhttps://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/19+Neighborhood+crime+report.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/19+Neighborhood+crime+report.jpg',
    },
  },

  'top-ranked-states-education': {
    path: 'top-ranked-states-education',
    singlePage: true,
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Top ranked states for education - US map',
    description:
      'Explore the education rankings map by state and down to neighborhood. #1 Massachusetts, #2 Maryland, #3 Connecticut.',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/20+top+ranked+states+for+public+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/20+top+ranked+states+for+public+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/20+top+ranked+states+for+public+education.jpg',
    },
  },

  'smoking-rates-by-state': {
    path: 'smoking-rates-by-state',
    singlePage: true,
    featureConst: SMOKING_RATE,
    name: 'Smoking rates by state map | Map AI',
    description:
      'Explore smoking rates by US state on an interactive map, plus health, income and demographic data down to 0.5-mile areas.',
    keywords: ['smoking rates by state', 'smoking rates by state map'],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/21+smoking+rates+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/21+smoking+rates+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/21+smoking+rates+by+state.jpg',
    },
  },

  'population-density-by-city': {
    path: 'population-density-by-city',
    cityLabel: 'population density map',
    cityBlurb: 'See where people cluster, with growth and median age.',
    featureConst: PEOPLE_PER_MI2,
    name: 'Population density by city map, United States - down to 0.5 mi',
    description:
      'Explore the population density map by city, with growth trends, median age and age-gender breakdown, plus detailed local data.',
    keywords: [
      'population density by city united states',
      'population density by city map',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/22+population+density+by+city+united+states.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/22+population+density+by+city+united+states.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/22+population+density+by+city+united+states.jpg',
    },
  },

  'cities-by-population-density': {
    path: 'cities-by-population-density',
    singlePage: true,
    canonicalPath: 'population-density-by-city',
    featureConst: PEOPLE_PER_MI2,
    name: 'Cities by population density map | Map AI',
    description:
      'Explore the population density map by city, with growth trends, median age and age-gender breakdown, plus detailed local data.',
    keywords: [
      'population density by city united states',
      'cities by population density',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/22+population+density+by+city+united+states.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/22+population+density+by+city+united+states.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/22+population+density+by+city+united+states.jpg',
    },
  },

  'map-of-median-household-income': {
    path: 'map-of-median-household-income',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'Map of median household income | Map AI',
    description:
      'View the map of median household income across the US, from state to 0.5-mile neighborhoods. Includes trends and comparisons.',
    keywords: ['map of median household income'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/24+map+of+median+household+income.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/24+map+of+median+household+income.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/24+map+of+median+household+income.jpg',
    },
  },

  'map-of-median-income': {
    path: 'map-of-median-income',
    singlePage: true,
    featureConst: MEDIAN_INCOME,
    name: 'Map of median income - from counties to neighborhoods',
    description:
      'Explore the map of median income by state and neighborhood. Includes household, family & individual income data with 0.5-mile granularity.',
    keywords: ['map of median income'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/24+map+of+median+household+income.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/24+map+of+median+household+income.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/24+map+of+median+household+income.jpg',
    },
  },

  'homeownership-rate-by-age': {
    path: 'homeownership-rate-by-age',
    singlePage: true,
    featureConst: HOME_OWNERSHIP_RATIO,
    name: 'Homeownership rate by age - US map',
    description:
      'Discover US homeownership rates by age. Use our interactive map to analyze patterns by income and demographics, down to 0.5-mile areas.',
    keywords: ['homeownership rate by age'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/26+homeownership+rate+by+age.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/26+homeownership+rate+by+age.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/26+homeownership+rate+by+age.jpg',
    },
  },

  'median-household-income-by-zip': {
    path: 'median-household-income-by-zip',
    cityLabel: 'median household income map by ZIP code',
    cityBlurb: 'Compare household income between ZIP codes.',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income by ZIP code map | Map AI',
    description:
      'Explore median household income by ZIP code with data down to 0.5-mile detail. Compare income by region, city, and neighborhood in one map.',
    keywords: ['median household income by zip code map'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'demographic-map-by-zip-code': {
    path: 'demographic-map-by-zip-code',
    cityLabel: 'demographic map by ZIP code',
    cityBlurb: 'Age, race, income and household size by area.',
    featureConst: POPULATION_GROWTH_TREND,
    name: 'Demographic map by ZIP code',
    description:
      'View the demographic map by ZIP code: age, race, income and more. Interactive and detailed down to 0.5-mile areas across the US.',
    keywords: ['demographic map by zip code'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/28+demographic+map+by+zip+code.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/28+demographic+map+by+zip+code.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/28+demographic+map+by+zip+code.jpg',
    },
  },

  'florida-crime-rate-by-county-map': {
    path: 'florida-crime-rate-by-county-map',
    singlePage: true,
    featureConst: CRIME_RATE,
    name: 'Florida crime rate by county map | Map AI',
    description:
      'Get clear Florida crime rate by county map. Total crime, community, business, violent, property.',
    keywords: [
      'florida crime rate by county map',
      'population density by us county',
      'us population density by county',
      'population density by county usa',
      'u.s. population density by county',
      'u.s. counties by population density',
      'us counties by population density',
      'us population density map by county',
      'counties by population density',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'us-population-density-by-county': {
    path: 'us-population-density-by-county',
    singlePage: true,
    featureConst: PEOPLE_PER_MI2,
    name: 'US population density map by county | Map AI',
    description:
      'Explore an interactive US map of population density by county. Zoom in for 100+ data insights for business and lifestyle decisions, at 1-mile precision.',
    keywords: [
      'population density by us county',
      'us population density by county',
      'population density by county usa',
      'u.s. population density by county',
      'u.s. counties by population density',
      'us counties by population density',
      'us population density ai map by county',
      'u.s. population density map by county',
      'population density by county',
      'counties by population density',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/30+u.s.+population+density+map+by+county.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/30+u.s.+population+density+map+by+county.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/30+u.s.+population+density+map+by+county.jpg',
    },
  },

  'heart-disease-by-state-map': {
    path: 'heart-disease-by-state-map',
    singlePage: true,
    featureConst: HEART_DISEASES_PREVALENCE,
    name: 'Heart disease by state - Map AI',
    description:
      'Explore the heart disease rate map by state. View trends, risk factors and health disparities, mapped down to 0.5-mile areas for local insights.',
    keywords: ['heart disease by state map'],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/31+heart+disease+by+state+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/31+heart+disease+by+state+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/31+heart+disease+by+state+map.jpg',
    },
  },

  // Keyword pages below are `singlePage`: the /:city/:feature cross-product
  // would repeat their name and description verbatim on 19 more URLs.

  'median-age-map': {
    path: 'median-age-map',
    featureConst: MEDIAN_AGE,
    name: 'Median age map US - by county and neighborhood',
    description:
      'Explore the median age map of the US, from state and county down to 0.5-mile neighborhoods. Compare age against income, housing and demographics.',
    keywords: [
      'median age map',
      'us median age map',
      'median age map by county',
      'average age map usa',
      'median age by zip code map',
    ],
    level: COUNTY_LEVEL_LAYER,
    singlePage: true,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/12+median+age+for+usa+every+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/12+median+age+for+usa+every+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/12+median+age+for+usa+every+state.jpg',
    },
  },

  'adult-obesity-map': {
    path: 'adult-obesity-map',
    featureConst: OBESITY_RATE,
    name: 'Adult obesity map US - obesity rates by county',
    description:
      'Explore the adult obesity map of the US. Compare obesity rates by state, county and 0.5-mile neighborhood alongside income, diet and health data.',
    keywords: [
      'adult obesity map',
      'obesity map',
      'obesity rate map',
      'obesity rates by county map',
      'obesity by state map',
      'us obesity map',
    ],
    level: COUNTY_LEVEL_LAYER,
    singlePage: true,
  },

  'traffic-intensity-map': {
    path: 'traffic-intensity-map',
    featureConst: CUMULATIVE_TRAFFIC_INTENSITY,
    name: 'Traffic intensity map US - daily vehicle miles traveled',
    description:
      'Explore the traffic intensity map of the US. See daily vehicle miles traveled down to 0.5-mile areas to judge how busy and visible a location is.',
    keywords: [
      'traffic intensity map',
      'traffic volume map',
      'traffic density map',
      'vehicle miles traveled map',
      'daily traffic map usa',
      'road traffic map by area',
    ],
    level: H3_RES5_LEVEL_LAYER,
    singlePage: true,
  },

  'population-growth-map': {
    path: 'population-growth-map',
    featureConst: POPULATION_GROWTH_TREND,
    name: 'Population growth map US - annual growth by area',
    description:
      'Explore the population growth map of the US. See annual growth and decline by state, county and 0.5-mile neighborhood, with income and housing context.',
    keywords: [
      'population growth map',
      'us population growth map',
      'population growth by county map',
      'population change map',
      'population growth rate map',
      'fastest growing areas map',
    ],
    level: COUNTY_LEVEL_LAYER,
    singlePage: true,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/28+demographic+map+by+zip+code.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/28+demographic+map+by+zip+code.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/28+demographic+map+by+zip+code.jpg',
    },
  },

  'retail-site-selection': {
    path: 'retail-site-selection',
    featureConst: MEDIAN_INCOME,
    name: 'Retail site selection map - find the best store location',
    description:
      'A retail site selection map for the US. Compare spending power, competitors, traffic and demographics down to 0.5-mile areas before you sign a lease.',
    keywords: [
      'retail site selection',
      'retail site selection map',
      'site selection for retail stores',
      'retail location analysis',
      'store location analysis',
      'best location for a retail store',
    ],
    level: COUNTY_LEVEL_LAYER,
    singlePage: true,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/24+map+of+median+household+income.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/24+map+of+median+household+income.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/24+map+of+median+household+income.jpg',
    },
  },

  'us-demographics-map-for-small-business': {
    path: 'us-demographics-map-for-small-business',
    featureConst: POPULATION,
    name: 'US demographics map for small business owners',
    description:
      'A free US demographics map built for small business: population, age, income and spending down to 0.5-mile areas. Check a location before you commit.',
    keywords: [
      'us demographics map',
      'demographics map for small business',
      'small business location demographics',
      'customer demographics map',
      'demographic data for small business',
      'market demographics map',
    ],
    level: COUNTY_LEVEL_LAYER,
    singlePage: true,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/28+demographic+map+by+zip+code.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/28+demographic+map+by+zip+code.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/28+demographic+map+by+zip+code.jpg',
    },
  },
};

export const CITIES: Record<string, CityCfg> = {
  'new-york': {
    path: 'new-york',
    name: 'New York',
    center: [-74.006, 40.7128],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['NY', 'NYC'],
    description:
      'Global capital of finance and culture with unmatched transit and dense, walkable neighborhoods. Diverse job market and constant foot traffic make it ideal for data-driven location decisions.',
  },
  'los-angeles': {
    path: 'los-angeles',
    name: 'Los Angeles',
    center: [-118.2437, 34.0522],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['LA'],
    description:
      'Sprawling coastal metro anchored by entertainment, media, and tech. Sunny climate and car-first mobility shape retail clusters and neighborhood demand patterns.',
  },
  boston: {
    path: 'boston',
    name: 'Boston',
    center: [-71.0589, 42.3601],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    description:
      'Historic, compact city with top universities and a strong biotech corridor. High foot traffic, transit access, and affluent suburbs drive premium consumer demand.',
  },
  chicago: {
    path: 'chicago',
    name: 'Chicago',
    center: [-87.6298, 41.8781],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CHI'],
    description:
      'Lakefront hub with iconic architecture and a diversified economy. Dense core and distinct neighborhoods offer varied demographics and spending profiles.',
  },
  houston: {
    path: 'houston',
    name: 'Houston',
    center: [-95.3698, 29.7604],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['HOU'],
    description:
      'Energy and healthcare powerhouse with rapid suburban growth. Large trade area, diverse communities, and business-friendly climate shape consumer patterns.',
  },
  phoenix: {
    path: 'phoenix',
    name: 'Phoenix',
    center: [-112.074, 33.4484],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['PHX'],
    description:
      'Fast-growing desert metro with abundant sunshine and new housing. Expanding suburbs and inbound migration drive evolving retail and service demand.',
  },
  philadelphia: {
    path: 'philadelphia',
    name: 'Philadelphia',
    center: [-75.1652, 39.9526],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['PHL', 'Philly'],
    description:
      'Historic East Coast city with strong eds-and-meds anchors. Rowhouse neighborhoods and transit corridors create stable local catchments for businesses.',
  },
  'san-antonio': {
    path: 'san-antonio',
    name: 'San Antonio',
    center: [-98.4936, 29.4241],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SAT'],
    description:
      'Family-friendly market known for the River Walk and military presence. Growing tech and manufacturing add to steady household formation.',
  },
  'san-diego': {
    path: 'san-diego',
    name: 'San Diego',
    center: [-117.1611, 32.7157],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SD'],
    description:
      'Coastal city with mild climate, strong biotech, and a major naval base. Tourism and high quality of life support premium retail and services.',
  },
  dallas: {
    path: 'dallas',
    name: 'Dallas',
    center: [-96.797, 32.7767],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DAL', 'DFW'],
    description:
      'Corporate HQ magnet with pro-business policies and rapid in-migration. Expansive suburbs create multiple high-growth trade areas.',
  },
  'san-jose': {
    path: 'san-jose',
    name: 'San Jose',
    center: [-121.8863, 37.3382],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SJ'],
    description:
      'Heart of Silicon Valley and high-income tech workforce. Suburban campuses and commuting patterns drive daytime population shifts.',
  },
  austin: {
    path: 'austin',
    name: 'Austin',
    center: [-97.7431, 30.2672],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['ATX'],
    description:
      'Booming tech and creative hub with a vibrant music scene. Strong startup activity and rapid population growth reshape local demand.',
  },
  jacksonville: {
    path: 'jacksonville',
    name: 'Jacksonville',
    center: [-81.6557, 30.3322],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['JAX'],
    description:
      'Florida’s largest city by land area with port, logistics, and naval anchors. Coastal lifestyle and suburban expansion drive housing and retail growth.',
  },
  'fort-worth': {
    path: 'fort-worth',
    name: 'Fort Worth',
    center: [-97.3308, 32.7555],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['FTW'],
    description:
      'Western heritage meets modern aerospace and defense. Paired with Dallas in the DFW region, it offers diverse neighborhoods and steady in-migration.',
  },
  columbus: {
    path: 'columbus',
    name: 'Columbus',
    center: [-82.9988, 39.9612],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CMH'],
    description:
      'State capital with a large university and growing innovation scene. Stable employment base supports consistent consumer demand.',
  },
  charlotte: {
    path: 'charlotte',
    name: 'Charlotte',
    center: [-80.8431, 35.2271],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CLT'],
    description:
      'Sun Belt banking center with fast-rising population. New master-planned communities and light-rail growth corridors create fresh retail nodes.',
  },
  'san-francisco': {
    path: 'san-francisco',
    name: 'San Francisco',
    center: [-122.4194, 37.7749],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SF'],
    description:
      'Iconic, walkable core on a dramatic bay-and-hills landscape. Tech, tourism, and premium dining fuel dense, high-spend micromarkets.',
  },
  indianapolis: {
    path: 'indianapolis',
    name: 'Indianapolis',
    center: [-86.1581, 39.7684],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['Indy'],
    description:
      'Logistics and sports hub with an affordable cost of living. Steady suburban growth and major events shape seasonal foot traffic.',
  },
  seattle: {
    path: 'seattle',
    name: 'Seattle',
    center: [-122.3321, 47.6062],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SEA'],
    description:
      'Waterfront tech hub with global brands and a major port. High incomes and outdoor lifestyle drive premium consumer segments.',
  },
  denver: {
    path: 'denver',
    name: 'Denver',
    center: [-104.9903, 39.7392],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DEN'],
    description:
      'Mile-High City at the base of the Rockies with strong aerospace and tech. Active lifestyle and rapid migration power new neighborhood hotspots.',
  },
  'washington-dc': {
    path: 'washington-dc',
    name: 'Washington, D.C.',
    center: [-77.0369, 38.9072],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DC'],
    description:
      'Nation’s capital with federal employment, think tanks, and tourism. Transit-rich core and stable incomes support consistent local demand.',
  },
  detroit: {
    path: 'detroit',
    name: 'Detroit',
    center: [-83.0458, 42.3314],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DET'],
    description:
      'Automotive heritage city with ongoing downtown revitalization. Manufacturing base and affordable housing create value-focused opportunities.',
  },
  nashville: {
    path: 'nashville',
    name: 'Nashville',
    center: [-86.7816, 36.1627],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['BNA'],
    description:
      'Music City with a strong healthcare sector and booming tourism. Rapid population growth fuels new retail, dining, and entertainment clusters.',
  },
};

export const OVERRIDES: OverrideCfg[] = [];

export const buildUrlCityFeature = (cityPath: string, featurePath: string) =>
  `/${cityPath}/${featurePath}`;

export const buildUrlFeature = (featurePath: string) => `/${featurePath}`;

/**
 * Longest `"<city> <cityLabel>"` that still leaves room for the brand suffix
 * inside Google's ~60-char title window.
 */
const CITY_TITLE_BRAND_BUDGET = 45;

/** `"New York crime rate map | Map AI"` - brand dropped when the phrase is long. */
export function buildCityTitle(cityName: string, feat: FeatureCfg): string {
  const phrase = `${cityName} ${feat.cityLabel ?? feat.name}`;
  return phrase.length > CITY_TITLE_BRAND_BUDGET
    ? phrase
    : `${phrase} | Map AI`;
}

export function buildCityDescription(
  cityName: string,
  feat: FeatureCfg
): string {
  const label = feat.cityLabel ?? feat.name;
  const lead = `Explore the ${label} for ${cityName}, down to 0.5-mile neighborhoods.`;
  return feat.cityBlurb ? `${lead} ${feat.cityBlurb}` : lead;
}

export function pickPageMeta(params: {
  cityPath?: string | null;
  featurePath: string;
}): PageMeta | null {
  const feat = FEATURES[params.featurePath];
  if (!feat) return null;

  // A merged variant keeps serving its URL but hands its ranking to the winner.
  const canonicalFeaturePath = feat.canonicalPath ?? feat.path;

  const applyOverrides = (o?: OverrideCfg) => ({
    keywords: (o?.keywords ?? feat.keywords) as PageMeta['keywords'],
    images: { ...feat.images, ...(o?.images ?? {}) } as PageMeta['images'],
    level: (o?.level ?? feat.level) as PageMeta['level'],
  });

  if (params.cityPath) {
    if (feat.singlePage || feat.canonicalPath) return null;
    const city = CITIES[params.cityPath];
    if (!city) return null;

    const o = OVERRIDES.find(
      (x) => x.feature === feat.path && x.city === city.path
    );
    const merged = applyOverrides(o);

    return {
      urlFull: `/${city.path}/${feat.path}`,
      canonicalUrl: `/${city.path}/${feat.path}`,
      title: o?.title ?? buildCityTitle(city.name, feat),
      description: o?.description ?? buildCityDescription(city.name, feat),
      city,
      feature: feat,
      ...merged,
    };
  }

  const o = OVERRIDES.find((x) => x.feature === feat.path && !x.city);
  const merged = applyOverrides(o);

  return {
    urlFull: `/${feat.path}`,
    canonicalUrl: `/${canonicalFeaturePath}`,
    title: o?.title ?? feat.name,
    description: o?.description ?? feat.description,
    feature: feat,
    ...merged,
  };
}
